import { Box, Card, CardContent, CardHeader, Chip, Divider, Grid2, Typography } from "@mui/material"
import { StatusChip } from "components/StatusChip/StatusChip"
import { Examination } from "graphql/types"

type ExaminationInfoProps = {
  examination: Examination
  showOperatingModes?: boolean
  compact?: boolean
}

export const ExaminationInfo = ({
  examination,
  showOperatingModes = true,
  compact = false,
}: ExaminationInfoProps) => {
  // Get operating modes from the examination
  const operatingModes = examination.operatingModes || []

  // Get device names from deviceSetup
  const deviceNames =
    examination.deviceSetup
      ?.map((setup) => setup.device.name)
      .filter(Boolean)
      .join(", ") || "Keine Geräte konfiguriert"

  if (compact) {
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle2" color="text.secondary">
              Prüfung: <strong>{examination.name}</strong>
            </Typography>
          </Grid2>
          {showOperatingModes && operatingModes.length > 0 && (
            <Grid2 size={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Betriebsmodi:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {operatingModes.map((mode, index) => (
                  <Chip key={index} label={mode} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Box>
    )
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title="Prüfungsinformationen" titleTypographyProps={{ variant: "h6" }} />
      <CardContent>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <Typography variant="body2" color="text.secondary">
              Prüfung
            </Typography>
            <Typography variant="body1" gutterBottom>
              {examination.name}
            </Typography>
          </Grid2>

          <Grid2 size={6}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <StatusChip status={examination.status} />
          </Grid2>

          <Grid2 size={6}>
            <Typography variant="body2" color="text.secondary">
              Norm
            </Typography>
            <Typography variant="body1" gutterBottom>
              {examination.norm?.name || "N/A"}
            </Typography>
          </Grid2>

          <Grid2 size={6}>
            <Typography variant="body2" color="text.secondary">
              Akkreditiert
            </Typography>
            <Typography variant="body1" gutterBottom>
              {examination.isAccredited ? "Ja" : "Nein"}
            </Typography>
          </Grid2>

          {examination.startTimestamp && (
            <Grid2 size={6}>
              <Typography variant="body2" color="text.secondary">
                Startzeit
              </Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(examination.startTimestamp).toLocaleString("de-DE")}
              </Typography>
            </Grid2>
          )}

          {examination.endTimestamp && (
            <Grid2 size={6}>
              <Typography variant="body2" color="text.secondary">
                Endzeit
              </Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(examination.endTimestamp).toLocaleString("de-DE")}
              </Typography>
            </Grid2>
          )}

          <Grid2 size={12}>
            <Typography variant="body2" color="text.secondary">
              Geprüfte Geräte
            </Typography>
            <Typography variant="body1" gutterBottom>
              {deviceNames}
            </Typography>
          </Grid2>

          {examination.specialArrangements && (
            <Grid2 size={12}>
              <Typography variant="body2" color="text.secondary">
                Besondere Vereinbarungen
              </Typography>
              <Typography variant="body1" gutterBottom>
                {examination.specialArrangements}
              </Typography>
            </Grid2>
          )}

          {examination.calculation && examination.calculation.offerText && (
            <Grid2 size={12}>
              <Typography variant="body2" color="text.secondary">
                Angebotstext
              </Typography>
              <Typography variant="body1" gutterBottom>
                {examination.calculation.offerText}
              </Typography>
            </Grid2>
          )}

          {showOperatingModes && operatingModes.length > 0 && (
            <>
              <Grid2 size={12}>
                <Divider sx={{ my: 1 }} />
              </Grid2>
              <Grid2 size={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Konfigurierte Betriebsmodi
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {operatingModes.map((mode, index) => (
                    <Chip key={index} label={mode} color="primary" variant="outlined" />
                  ))}
                </Box>
                {examination.numberOfRuns && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Anzahl der Durchläufe: {examination.numberOfRuns}
                  </Typography>
                )}
              </Grid2>
            </>
          )}
        </Grid2>
      </CardContent>
    </Card>
  )
}
